import { EventClient, InvoicePaymentLinkRequest, InvoicePaymentToken } from '@kalos/kalos-rpc';
import { Button, Card, Input, Label } from '@kalos/ui';
import { HelcimCheckout, HelcimCheckoutComplete } from 'kalos-modules';
import { Copy, ExternalLink, Link as LinkIcon, TestTube, X } from 'lucide-react';
import { type FC, useCallback, useMemo, useState } from 'react';

import { ENDPOINT } from '../../../constants';
import { useKalosLogoQuery } from '../../../hooks/react-query/useKalosLogoQuery';
import { EventClientService } from '../../../tools/helpers';

interface InvoicePaymentLinkGeneratorProps {
  invoiceId: number;
  jobNumber?: string;
}

export const InvoicePaymentLinkGenerator: FC<InvoicePaymentLinkGeneratorProps> = ({
  invoiceId,
  jobNumber,
}) => {
  const kalosLogo = useKalosLogoQuery();
  const [daysToExpire, setDaysToExpire] = useState<number>(30);
  const [isGenerating, setIsGenerating] = useState(false);
  const [generatedLink, setGeneratedLink] = useState<string | null>(null);
  const [generatedToken, setGeneratedToken] = useState<string | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [isAlreadyPaid, setIsAlreadyPaid] = useState(false);
  const [copied, setCopied] = useState(false);
  const [showTestForm, setShowTestForm] = useState(false);
  const [testPaymentData, setTestPaymentData] = useState<any>(null);
  const [isLoadingTest, setIsLoadingTest] = useState(false);
  const [testPaymentSuccess, setTestPaymentSuccess] = useState(false);
  const [testPaymentIntentId, setTestPaymentIntentId] = useState<string | null>(null);

  const handleGenerateLink = useCallback(async () => {
    try {
      setIsGenerating(true);
      setError(null);
      setIsAlreadyPaid(false);
      setGeneratedLink(null);
      setGeneratedToken(null);

      const request = InvoicePaymentLinkRequest.create({
        invoiceId,
        daysToExpire,
        paymentType: 'helcim',
      });

      const response = await EventClientService.createInvoicePaymentLink(request);

      if (response?.token && response?.paymentUrl) {
        setGeneratedToken(response.token);
        setGeneratedLink(response.paymentUrl);
      } else {
        throw new Error('No token or payment URL received from server');
      }
    } catch (err) {
      const errorMessage = err instanceof Error ? err.message : 'Failed to generate payment link';
      // Check if the error indicates the invoice is already paid
      if (
        errorMessage.toLowerCase().includes('already fully paid') ||
        errorMessage.toLowerCase().includes('already paid')
      ) {
        setIsAlreadyPaid(true);
      } else {
        setError(errorMessage);
      }
      console.error('Failed to generate payment link:', err);
    } finally {
      setIsGenerating(false);
    }
  }, [invoiceId, daysToExpire]);

  const handleCopyLink = useCallback(async () => {
    if (generatedLink) {
      try {
        await navigator.clipboard.writeText(generatedLink);
        setCopied(true);
        setTimeout(() => setCopied(false), 2000);
      } catch (err) {
        console.error('Failed to copy link:', err);
      }
    }
  }, [generatedLink]);

  const handleCopyToken = useCallback(async () => {
    if (generatedToken) {
      try {
        await navigator.clipboard.writeText(generatedToken);
        setCopied(true);
        setTimeout(() => setCopied(false), 2000);
      } catch (err) {
        console.error('Failed to copy token:', err);
      }
    }
  }, [generatedToken]);

  const handleOpenLink = useCallback(() => {
    if (generatedLink) {
      window.open(generatedLink, '_blank');
    }
  }, [generatedLink]);

  const handleTestPaymentForm = useCallback(async () => {
    if (!generatedToken) return;

    try {
      setIsLoadingTest(true);
      setError(null);

      const eventClient = new EventClient(ENDPOINT);
      const request = InvoicePaymentToken.create({ token: generatedToken });
      const response = await eventClient.getPublicInvoicePaymentData(request);

      setTestPaymentData(response);
      setShowTestForm(true);
    } catch (err) {
      const errorMessage = err instanceof Error ? err.message : 'Failed to load payment data';
      setError(`Test failed: ${errorMessage}`);
      console.error('Failed to load test payment data:', err);
    } finally {
      setIsLoadingTest(false);
    }
  }, [generatedToken]);

  const handleCloseTestForm = useCallback(() => {
    setShowTestForm(false);
    setTestPaymentData(null);
    setTestPaymentSuccess(false);
    setTestPaymentIntentId(null);
  }, []);

  const handleTestSuccess = useCallback((paymentIntentId: string) => {
    console.log('Test payment successful!', paymentIntentId);
    setTestPaymentIntentId(paymentIntentId);
    setTestPaymentSuccess(true);
  }, []);

  const amount = useMemo(() => {
    if (!testPaymentData?.totals) return 0;
    const totalAmount = Number(testPaymentData.totals.grandTotal || 0);
    const paidAmount =
      testPaymentData.payments?.reduce((sum: number, payment: any) => {
        return sum + Number(payment.amountCollected || 0);
      }, 0) || 0;
    return totalAmount - paidAmount;
  }, [testPaymentData]);

  // Build line items from test payment data
  const lineItems = useMemo(() => {
    if (!testPaymentData) return [];

    const items = [];
    const event = testPaymentData.event;

    // Add services performed rows (legacy)
    if (event?.servicesperformedrow1) {
      const unitPrice = parseFloat(event.totalamountrow1 || '0');
      items.push({
        description: event.servicesperformedrow1,
        quantity: 1,
        unitPrice,
        total: unitPrice,
      });
    }

    if (event?.servicesperformedrow2) {
      const unitPrice = parseFloat(event.totalamountrow2 || '0');
      items.push({
        description: event.servicesperformedrow2,
        quantity: 1,
        unitPrice,
        total: unitPrice,
      });
    }

    if (event?.servicesperformedrow3) {
      const unitPrice = parseFloat(event.totalamountrow3 || '0');
      items.push({
        description: event.servicesperformedrow3,
        quantity: 1,
        unitPrice,
        total: unitPrice,
      });
    }

    if (event?.servicesperformedrow4) {
      const unitPrice = parseFloat(event.totalamountrow4 || '0');
      items.push({
        description: event.servicesperformedrow4,
        quantity: 1,
        unitPrice,
        total: unitPrice,
      });
    }

    // Add material items (quotables) - only billable and included
    if (testPaymentData.materials) {
      const includedMaterials = testPaymentData.materials.filter(
        (material: any) => material.isBillable && material.includeInInvoice,
      );

      for (const material of includedMaterials) {
        const unitPrice = material.quotedPrice || 0;
        const quantity = material.quantity || 1;
        const total = unitPrice * quantity;

        items.push({
          description: material.description || 'Material',
          quantity,
          unitPrice,
          total,
        });
      }
    }

    return items;
  }, [testPaymentData]);

  const subtotal = useMemo(() => {
    return testPaymentData?.totals?.subtotal || 0;
  }, [testPaymentData]);

  const discountAmount = useMemo(() => {
    return testPaymentData?.totals?.discountAmount || 0;
  }, [testPaymentData]);

  const taxAmount = useMemo(() => {
    return testPaymentData?.totals?.taxAmount || 0;
  }, [testPaymentData]);

  const propertyAddress = useMemo(() => {
    if (!testPaymentData?.property) return undefined;
    const { address, city, state, zip } = testPaymentData.property;
    if (!address) return undefined;
    return `${address}, ${city}, ${state} ${zip}`;
  }, [testPaymentData]);

  return (
    <Card className="p-6">
      <div className="mb-4 flex items-center">
        <LinkIcon className="mr-2 h-5 w-5 text-blue-600" />
        <h3 className="text-lg font-semibold text-gray-900">Generate Payment Link</h3>
      </div>

      <div className="space-y-4">
        {/* Invoice Info */}
        <div className="rounded-lg bg-gray-50 p-4">
          <div className="space-y-2 text-sm">
            <div className="flex justify-between">
              <span className="text-gray-600">Invoice ID:</span>
              <span className="font-medium text-gray-900">{invoiceId}</span>
            </div>
            {jobNumber && (
              <div className="flex justify-between">
                <span className="text-gray-600">Job Number:</span>
                <span className="font-medium text-gray-900">{jobNumber}</span>
              </div>
            )}
          </div>
        </div>

        {/* Link Expiration Input */}
        <div>
          <Label htmlFor="daysToExpire">Link Expiration (days)</Label>
          <Input
            id="daysToExpire"
            type="number"
            min={1}
            max={365}
            value={daysToExpire}
            onChange={(e) => setDaysToExpire(Number(e.target.value))}
            className="mt-1"
          />
          <p className="mt-1 text-xs text-gray-500">
            The payment link will expire after {daysToExpire} day{daysToExpire !== 1 ? 's' : ''}
          </p>
        </div>

        {/* Generate Button */}
        <Button
          onClick={handleGenerateLink}
          disabled={isGenerating || !invoiceId}
          className="w-full"
        >
          {isGenerating ? (
            <>
              <div className="mr-2 h-4 w-4 animate-spin rounded-full border-2 border-white border-t-transparent" />
              Generating...
            </>
          ) : (
            <>
              <LinkIcon className="mr-2 h-4 w-4" />
              Generate Payment Link
            </>
          )}
        </Button>

        {/* Error Display */}
        {error && (
          <div className="rounded-lg border border-red-200 bg-red-50 p-4">
            <p className="text-sm text-red-800">{error}</p>
          </div>
        )}

        {/* Invoice Already Paid Display */}
        {isAlreadyPaid && (
          <div className="space-y-3 rounded-lg border border-green-200 bg-green-50 p-4">
            <div className="flex items-center">
              <div className="flex h-8 w-8 items-center justify-center rounded-full bg-green-100">
                <span className="text-green-600">✓</span>
              </div>
              <span className="ml-2 font-medium text-green-800">Invoice Already Paid</span>
            </div>
            <p className="text-sm text-gray-600">
              This invoice has already been paid in full. No payment link is needed.
            </p>
            <p className="text-xs text-gray-500">
              If you believe this is an error, please check the payment records in the Invoice tab.
            </p>
          </div>
        )}

        {/* Generated Link Display */}
        {generatedLink && generatedToken && (
          <div className="space-y-3 rounded-lg border border-green-200 bg-green-50 p-4">
            <div className="flex items-center">
              <div className="flex h-8 w-8 items-center justify-center rounded-full bg-green-100">
                <span className="text-green-600">✓</span>
              </div>
              <span className="ml-2 font-medium text-green-800">Payment Link Generated!</span>
            </div>

            {/* Token */}
            <div>
              <Label className="text-xs text-gray-600">Token:</Label>
              <div className="mt-1 flex items-center space-x-2">
                <code className="flex-1 rounded bg-white p-2 text-xs text-gray-700">
                  {generatedToken}
                </code>
                <Button size="sm" variant="outline" onClick={handleCopyToken} className="shrink-0">
                  <Copy className="h-4 w-4" />
                </Button>
              </div>
            </div>

            {/* Full URL */}
            <div>
              <Label className="text-xs text-gray-600">Payment URL:</Label>
              <div className="mt-1 flex items-center space-x-2">
                <code className="flex-1 overflow-x-auto rounded bg-white p-2 text-xs text-gray-700">
                  {generatedLink}
                </code>
                <Button size="sm" variant="outline" onClick={handleCopyLink} className="shrink-0">
                  <Copy className="h-4 w-4" />
                </Button>
                <Button size="sm" variant="outline" onClick={handleOpenLink} className="shrink-0">
                  <ExternalLink className="h-4 w-4" />
                </Button>
              </div>
            </div>

            {copied && (
              <div className="text-center text-sm text-green-600">Copied to clipboard!</div>
            )}

            <div className="text-xs text-gray-600">
              <p>✓ This link can be shared with the customer to make a payment</p>
              <p>✓ The link will expire in {daysToExpire} days</p>
              <p>✓ Customer can pay securely through Helcim</p>
            </div>

            {/* Test Button */}
            <Button
              onClick={handleTestPaymentForm}
              disabled={isLoadingTest}
              variant="outline"
              className="w-full"
            >
              {isLoadingTest ? (
                <>
                  <div className="mr-2 h-4 w-4 animate-spin rounded-full border-2 border-gray-600 border-t-transparent" />
                  Loading Test Form...
                </>
              ) : (
                <>
                  <TestTube className="mr-2 h-4 w-4" />
                  Test Payment Form
                </>
              )}
            </Button>
          </div>
        )}

        {/* Test Payment Form Modal */}
        {showTestForm && testPaymentData && (
          <div className="fixed inset-0 z-50 flex items-center justify-center bg-black bg-opacity-50 p-4">
            <div className="relative max-h-[90vh] w-full max-w-3xl overflow-y-auto rounded-lg bg-white shadow-xl">
              {/* Close Button */}
              <button
                onClick={handleCloseTestForm}
                className="absolute right-4 top-4 z-10 rounded-full bg-white p-2 shadow-md hover:bg-gray-100"
              >
                <X className="h-5 w-5 text-gray-600" />
              </button>

              {/* Payment Form or Success */}
              <div className="p-6 pt-16">
                {testPaymentSuccess ? (
                  <div className="space-y-4">
                    <HelcimCheckoutComplete
                      logoUrl={kalosLogo.data}
                      amount={amount}
                      transactionId={testPaymentIntentId || undefined}
                    />
                  </div>
                ) : amount <= 0 || (testPaymentData.totals?.totalDue ?? 0) <= 0 ? (
                  <div className="space-y-4 text-center">
                    <div className="mx-auto flex h-16 w-16 items-center justify-center rounded-full bg-green-100">
                      <span className="text-3xl text-green-600">✓</span>
                    </div>
                    <h3 className="text-xl font-semibold text-green-800">Invoice Already Paid</h3>
                    <p className="text-gray-600">
                      This invoice has already been paid in full. No further payment is required.
                    </p>
                    {testPaymentData.payments && testPaymentData.payments.length > 0 && (
                      <div className="mt-4 rounded-lg border border-gray-200 bg-gray-50 p-4 text-left">
                        <p className="mb-2 font-medium text-gray-700">Payment History:</p>
                        <ul className="space-y-1 text-sm text-gray-600">
                          {testPaymentData.payments.map((payment: any, index: number) => (
                            <li key={index} className="flex justify-between">
                              <span>{payment.type || 'Payment'}</span>
                              <span className="font-medium">
                                ${Number(payment.amountCollected || 0).toFixed(2)}
                              </span>
                            </li>
                          ))}
                        </ul>
                      </div>
                    )}
                    <Button onClick={handleCloseTestForm} className="mt-4">
                      Close
                    </Button>
                  </div>
                ) : testPaymentData.checkoutToken ? (
                  <HelcimCheckout
                    logoUrl={kalosLogo.data}
                    checkoutToken={testPaymentData.checkoutToken}
                    amount={amount}
                    description={`Test Payment - Invoice #${testPaymentData.invoice?.id || invoiceId}`}
                    metadata={{
                      invoiceId: testPaymentData.invoice?.id || invoiceId,
                      jobNumber: testPaymentData.event?.logJobNumber || jobNumber || '',
                      customerId: testPaymentData.customer?.id || 0,
                    }}
                    lineItems={lineItems}
                    subtotal={subtotal}
                    discountAmount={discountAmount}
                    taxAmount={taxAmount}
                    customerName={
                      testPaymentData.customer
                        ? `${testPaymentData.customer.firstname} ${testPaymentData.customer.lastname}`
                        : undefined
                    }
                    propertyAddress={propertyAddress}
                    onSuccess={handleTestSuccess}
                    onCancel={handleCloseTestForm}
                    onError={(error) => {
                      console.error('Helcim payment error:', error);
                      setError(`Payment error: ${error}`);
                    }}
                  />
                ) : (
                  <div className="rounded-lg border border-red-200 bg-red-50 p-4">
                    <p className="text-sm text-red-800">
                      Missing payment data. Checkout Token:{' '}
                      {testPaymentData.checkoutToken ? '✓' : '✗'}
                    </p>
                  </div>
                )}
              </div>
            </div>
          </div>
        )}
      </div>
    </Card>
  );
};
